import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

type ChartPoint = { name: string; value: number; fill: string };
type RevenuePoint = { month: string; revenus: number; commandes: number };
type WeeklyPoint = { day: string; produits: number; evenements: number; utilisateurs: number };

type AdminChartsProps = {
  revenueMonthly: RevenuePoint[];
  productsStatus: ChartPoint[];
  eventsStatus: ChartPoint[];
  usersRepartition: ChartPoint[];
  weeklyActivity: WeeklyPoint[];
};

const formatFCFA = (v: number) =>
  v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`;

const NAVY = '#1B2A4A';
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8CC7A';
const EMERALD = '#10B981';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-xs shadow-lg">
      <p className="font-bold text-navy mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name} : <span className="text-navy">{typeof p.value === 'number' && p.value > 1000 ? `${p.value.toLocaleString('fr-FR')} FCFA` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

const RevenueTooltip = ({ active, payload, label }: TooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-xs shadow-lg">
      <p className="font-bold text-navy mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name === 'revenus' ? 'Revenus' : 'Commandes'} :{' '}
          <span className="text-navy">
            {p.name === 'revenus' ? `${p.value.toLocaleString('fr-FR')} FCFA` : p.value}
          </span>
        </p>
      ))}
    </div>
  );
};

const RADIAN = Math.PI / 180;
interface PieLabelProps {
  cx: number; cy: number; midAngle: number; innerRadius: number;
  outerRadius: number; percent: number; name: string;
}
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: PieLabelProps) => {
  if (percent < 0.08) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="bold">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function AdminCharts({
  revenueMonthly,
  productsStatus,
  eventsStatus,
  usersRepartition,
  weeklyActivity,
}: AdminChartsProps) {
  return (
    <div className="space-y-5">
      {/* Row 1 : Revenue area + Weekly activity bar */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Area chart — Revenus mensuels */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center bg-navy/10 rounded-lg">
                <i className="ri-line-chart-line text-navy text-sm"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-navy">Revenus & Commandes</h3>
                <p className="text-xs text-gray-400">Évolution sur 7 mois</p>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revenueMonthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="adminRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={NAVY} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={NAVY} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="adminCmdGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GOLD} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={formatFCFA} width={40} />
              <Tooltip content={<RevenueTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="revenus" stroke={NAVY} strokeWidth={2.5} fill="url(#adminRevGrad)" name="revenus" dot={false} activeDot={{ r: 4 }} />
              <Area type="monotone" dataKey="commandes" stroke={GOLD} strokeWidth={2} fill="url(#adminCmdGrad)" name="commandes" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Grouped bar — Activité hebdomadaire */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-gold/20 rounded-lg">
              <i className="ri-bar-chart-grouped-line text-gold-dark text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Activité cette semaine</h3>
              <p className="text-xs text-gray-400">Produits · Événements · Utilisateurs</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyActivity} margin={{ top: 5, right: 10, left: 0, bottom: 0 }} barSize={10} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} width={25} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="produits" fill={NAVY} radius={[4, 4, 0, 0]} name="Produits" />
              <Bar dataKey="evenements" fill={GOLD} radius={[4, 4, 0, 0]} name="Événements" />
              <Bar dataKey="utilisateurs" fill={EMERALD} radius={[4, 4, 0, 0]} name="Utilisateurs" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2 : 3 Pie/Donut charts */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {/* Pie — Produits statuts */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center bg-navy/10 rounded-lg">
              <i className="ri-store-line text-navy text-xs"></i>
            </div>
            <h3 className="text-sm font-bold text-navy">Produits</h3>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={productsStatus} cx="50%" cy="50%" outerRadius={72} innerRadius={36}
                dataKey="value" labelLine={false} label={renderCustomLabel}>
                {productsStatus.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => [`${v}`, '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-col gap-1.5 mt-2">
            {productsStatus.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.fill }}></span>
                  <span className="text-xs text-gray-600">{s.name}</span>
                </div>
                <span className="text-xs font-bold text-navy">{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pie — Événements statuts */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center bg-gold/20 rounded-lg">
              <i className="ri-calendar-event-line text-gold-dark text-xs"></i>
            </div>
            <h3 className="text-sm font-bold text-navy">Événements</h3>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={eventsStatus} cx="50%" cy="50%" outerRadius={72} innerRadius={36}
                dataKey="value" labelLine={false} label={renderCustomLabel}>
                {eventsStatus.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => [`${v}`, '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-col gap-1.5 mt-2">
            {eventsStatus.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.fill }}></span>
                  <span className="text-xs text-gray-600">{s.name}</span>
                </div>
                <span className="text-xs font-bold text-navy">{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pie — Répartition utilisateurs */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-lg">
              <i className="ri-team-line text-emerald-700 text-xs"></i>
            </div>
            <h3 className="text-sm font-bold text-navy">Utilisateurs</h3>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={usersRepartition} cx="50%" cy="50%" outerRadius={72} innerRadius={36}
                dataKey="value" labelLine={false} label={renderCustomLabel}>
                {usersRepartition.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => [`${v}`, '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-col gap-1.5 mt-2">
            {usersRepartition.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.fill }}></span>
                  <span className="text-xs text-gray-600">{s.name}</span>
                </div>
                <span className="text-xs font-bold text-navy">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
